import { useEffect, useMemo, useRef, useState } from 'react';
import type { BattleEvent } from '../../lib/types';
import { Bar, Button, Modal } from '../ui';
import { formatNumber } from '../../lib/format';

/**
 * The encounter, played back — design PART 6.
 *
 * **The server has already decided everything.** By the time this mounts the
 * damage is banked, the pool has moved and the rank is settled; the events are
 * a transcript to animate, and nothing here can change an outcome. That is the
 * whole of PART 23 in one sentence, and it is why this component is allowed to
 * be as showy as it likes.
 *
 * **Target is 10–30 seconds, and the tick is what enforces it.** A twelve-turn
 * encounter is roughly thirty to forty events; at 340ms that lands around
 * fifteen seconds, with Skip for the player on their fifth attempt of the
 * evening who has seen it four times already. PART 6 is explicit that this must
 * not become a full battle simulator — there is no move selection, no switching
 * and no input, because there is nothing to decide.
 *
 * It is modelled on `war/BattleReplay.tsx` and deliberately not shared with it.
 * The two draw different fights: a war is six versus six with switching and a
 * winner, and this is one Pokemon against a wall where the only number that
 * matters is the running total. Merging them would mean a component with two
 * modes and a prop that decides which, which is how both get worse.
 */

const TICK_MS = 340;

interface Board {
  name: string;
  speciesId: number;
  isShiny: boolean;
  level: number;
  hp: number;
  maxHp: number;
  status: string | null;
}

export function BossBattle({
  events,
  fighterSprite,
  bossSprite,
  bossName,
  damage,
  outcome,
  totalDamage,
  rank,
  nextRank,
  milestones,
  phase,
  onClose,
}: {
  events: BattleEvent[];
  fighterSprite: string | null;
  bossSprite: string | null;
  bossName: string;
  damage: number;
  outcome: string;
  totalDamage: number;
  rank: number;
  nextRank: { rank: number; damage: number; needed: number } | null;
  milestones: Array<{ damage: number; label: string }>;
  phase: string | null;
  onClose: () => void;
}) {
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(true);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!playing || cursor >= events.length) return;
    const timer = window.setTimeout(() => setCursor((c) => c + 1), TICK_MS);
    return () => window.clearTimeout(timer);
  }, [cursor, playing, events.length]);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: 'smooth' });
  }, [cursor]);

  /**
   * Replays the transcript up to the cursor to derive the board.
   *
   * Recomputed from scratch each tick rather than mutated, so Skip works for
   * free and the numbers on screen can never drift from the events that
   * produced them — the same reasoning the war replay gives.
   */
  const board = useMemo(() => {
    // A record rather than two `let`s: the reducer assigns through closures,
    // and TypeScript narrows a closed-over `let` to `never` after the first
    // assignment, which turns every read below into a type error.
    const sides: Record<'a' | 'b', Board | null> = { a: null, b: null };
    const lines: Array<{ text: string; tone?: 'crit' | 'super' | 'faint' | 'hit' }> = [];
    let dealt = 0;

    const get = (side: 'a' | 'b') => sides[side];
    const set = (side: 'a' | 'b', next: Board) => {
      sides[side] = next;
    };

    for (const event of events.slice(0, cursor)) {
      switch (event.t) {
        case 'send':
          set(event.side, {
            name: event.name,
            speciesId: event.speciesId,
            isShiny: event.isShiny,
            level: event.level,
            hp: event.hp,
            maxHp: event.maxHp,
            status: null,
          });
          break;
        case 'move':
          lines.push({ text: `${get(event.side)?.name ?? '?'} used ${event.name}!` });
          break;
        case 'miss':
          lines.push({ text: 'It missed.' });
          break;
        case 'damage': {
          const target = event.side === 'a' ? 'b' : 'a';
          const current = get(target);
          if (current) set(target, { ...current, hp: event.hp });
          if (event.side === 'a') dealt += event.amount;
          if (event.crit) lines.push({ text: 'A CRITICAL HIT!', tone: 'crit' });
          if (event.note) lines.push({ text: event.note.toUpperCase(), tone: 'super' });
          lines.push({
            text: `${current?.name ?? '?'} took ${formatNumber(event.amount)} damage.`,
            tone: event.side === 'a' ? 'hit' : undefined,
          });
          break;
        }
        case 'heal': {
          const current = get(event.side);
          if (current) set(event.side, { ...current, hp: event.hp });
          lines.push({ text: `${current?.name ?? '?'} recovered ${formatNumber(event.amount)}.` });
          break;
        }
        case 'stat':
          lines.push({
            text: `${get(event.side)?.name ?? '?'}'s ${event.stat} ${event.delta > 0 ? 'rose' : 'fell'}.`,
          });
          break;
        case 'status': {
          const current = get(event.side);
          if (current) set(event.side, { ...current, status: event.status });
          lines.push({ text: `${current?.name ?? '?'} was ${event.status}ed.` });
          break;
        }
        case 'statusTick': {
          const current = get(event.side);
          if (current) set(event.side, { ...current, hp: event.hp });
          lines.push({ text: `${current?.name ?? '?'} was hurt by its ${event.status}.` });
          break;
        }
        case 'faint':
          lines.push({
            text: `${get(event.side)?.name ?? '?'} fainted!`,
            tone: 'faint',
          });
          break;
        default:
          break;
      }
    }

    return { fighter: sides.a, boss: sides.b, lines, dealt };
  }, [events, cursor]);

  const done = cursor >= events.length && events.length > 0;

  return (
    <Modal onClose={onClose} wide labelledBy="boss-battle-title">
      <div className="stack wb-battle">
        <h2 id="boss-battle-title" className="page-head__title">
          {board.fighter?.name ?? 'Your fighter'} vs {bossName}
          {phase && <span className="chip" style={{ marginLeft: 8 }}>{phase}</span>}
        </h2>

        <div className="wb-battle__field">
          <Combatant
            side={board.fighter}
            sprite={fighterSprite}
            label="Your fighter"
            // The player's own HP is shown as a proportion, because what matters
            // is how much longer they last — the exact number is not a decision
            // anybody can act on mid-encounter.
            showExact={false}
          />
          <div className="wb-battle__vs" aria-hidden>
            VS
          </div>
          <Combatant side={board.boss} sprite={bossSprite} label={bossName} mirrored showExact={false} />
        </div>

        {/* The running total, which is the only number the encounter is
            actually about. Sits above the log rather than below it so it is the
            thing in view while the log scrolls. */}
        <div className="pxwell wb-battle__tally">
          <span className="tiny muted">Damage dealt</span>
          <strong className="mono wb-battle__tally-figure">{formatNumber(board.dealt)}</strong>
        </div>

        <div ref={logRef} className="pxwell battle-log wb-battle__log">
          {board.lines.map((line, index) => (
            <div key={index} className={`tiny${line.tone ? ` wb-battle__line--${line.tone}` : ''}`}>
              {line.text}
            </div>
          ))}
        </div>

        <div className="row row--between">
          <div className="row" style={{ gap: 6 }}>
            <Button size="sm" onClick={() => setPlaying((p) => !p)} disabled={done}>
              {playing ? 'Pause' : 'Play'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setCursor(events.length);
                setPlaying(false);
              }}
              disabled={done}
            >
              Skip
            </Button>
          </div>
          {done && (
            <span className="chip">
              {outcome === 'REPELLED'
                ? 'Driven off!'
                : outcome === 'FAINTED'
                  ? 'Your fighter fell'
                  : 'Still standing'}
            </span>
          )}
        </div>

        {done && (
          <div className="pxframe wb-battle__result">
            <div className="wb-battle__result-head">Battle complete</div>

            <dl className="wb-battle__result-grid">
              <div>
                <dt>Damage contributed</dt>
                <dd className="mono">{formatNumber(damage)}</dd>
              </div>
              <div>
                <dt>Total event damage</dt>
                <dd className="mono">{formatNumber(totalDamage)}</dd>
              </div>
              <div>
                <dt>Current rank</dt>
                <dd className="mono">#{rank}</dd>
              </div>
              {nextRank && (
                <div>
                  <dt>Next rank</dt>
                  <dd className="mono">
                    #{nextRank.rank} — {formatNumber(nextRank.needed)} more
                  </dd>
                </div>
              )}
            </dl>

            {milestones.length > 0 && (
              <p className="tiny wb-battle__milestone">
                Milestone reached: {milestones.map((m) => m.label).join(', ')}
              </p>
            )}

            <Button onClick={onClose}>Close</Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Combatant({
  side,
  sprite,
  label,
  mirrored,
  showExact,
}: {
  side: Board | null;
  sprite: string | null;
  label: string;
  mirrored?: boolean;
  showExact: boolean;
}) {
  const ratio = side && side.maxHp > 0 ? side.hp / side.maxHp : 1;
  const fainted = side != null && side.hp <= 0;

  return (
    <div className={`wb-battle__side${mirrored ? ' wb-battle__side--right' : ''}`}>
      <div className="tiny muted">{label}</div>
      <img
        className={`pixel wb-battle__sprite${fainted ? ' wb-battle__sprite--out' : ''}`}
        src={sprite ?? ''}
        alt=""
        width={96}
        height={96}
      />
      <div className="tiny">
        {side ? (
          <>
            {side.isShiny && <span style={{ color: 'var(--gold-dark)' }}>✦ </span>}
            {side.name} <span className="muted">Lv {side.level}</span>
          </>
        ) : (
          <span className="muted">Waiting…</span>
        )}
      </div>
      <Bar ratio={ratio} variant={ratio > 0.25 ? 'egg' : 'default'} />
      {showExact && side && (
        <div className="tiny muted mono">
          {formatNumber(side.hp)} / {formatNumber(side.maxHp)}
        </div>
      )}
      {side?.status && <span className="chip chip--sm">{side.status}</span>}
    </div>
  );
}
